import { useState, useEffect } from 'react';
import { serviceApi } from '../../api/serviceApi';
import type { ServiceResponse } from '../../api/serviceApi';
import { specialtyApi, type SpecialtyResponse } from '../../api/specialtyApi';
import { useSalonId } from '../../hooks/useSalonId';
import { useToast } from '../../contexts/ToastContext';
import PrerequisiteAlertModal from '../../components/ui/PrerequisiteAlertModal';
import SearchableSelect from '../../components/ui/SearchableSelect';
import { TableSkeleton } from '../../components/ui/Skeleton';
import { EmptyState } from '../../components/ui/EmptyState';
import { Scissors } from 'lucide-react';

const ServiceListPage = () => {
    const salonId = useSalonId();
    const toast = useToast();
    const [services, setServices] = useState<ServiceResponse[]>([]);
    const [filteredServices, setFilteredServices] = useState<ServiceResponse[]>([]);
    const [specialties, setSpecialties] = useState<SpecialtyResponse[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);

    const [formData, setFormData] = useState({
        name: '',
        price: '',
        durationMinutes: '60',
        requiredSpecialtyId: ''
    });

    const [isEditing, setIsEditing] = useState(false);
    const [selectedServiceId, setSelectedServiceId] = useState<string | null>(null);
    const [showPrerequisiteAlert, setShowPrerequisiteAlert] = useState(false);

    // Load services and specialties on mount
    useEffect(() => {
        if (!salonId) return; // Don't load if no salon
        loadServices();
        loadSpecialties();
    }, [salonId]);

    const loadSpecialties = async () => {
        if (!salonId) return;
        try {
            const data = await specialtyApi.getSalonSpecialties(salonId);
            setSpecialties(data);
        } catch (error) {
            console.error('Error loading specialties:', error);
        }
    };

    // Filter services when search query changes
    useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredServices(services);
            return;
        }

        const query = searchQuery.toLowerCase();
        const filtered = services.filter(service =>
            service.name.toLowerCase().includes(query)
        );
        setFilteredServices(filtered);
    }, [searchQuery, services]);

    const loadServices = async () => {
        if (!salonId) return; // Guard for null salonId
        setIsLoading(true);
        try {
            const data = await serviceApi.getSalonServices(salonId);

            // Defensive check: ensure data is an array before sorting
            if (!Array.isArray(data)) {
                console.warn('Services data is not an array:', data);
                setServices([]);
                setFilteredServices([]);
                return;
            }

            // Sort alphabetically by name
            const sortedServices = data.sort((a, b) =>
                a.name.localeCompare(b.name, 'es', { sensitivity: 'base' })
            );

            setServices(sortedServices);
            setFilteredServices(sortedServices);
        } catch (error) {
            console.error('Error loading services:', error);
            setServices([]);
            setFilteredServices([]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCreateClick = () => {
        // Validation: Must have specialties first (requirement from user)
        if (specialties.length === 0) {
            setShowPrerequisiteAlert(true);
            return;
        }

        setIsEditing(false);
        setSelectedServiceId(null);
        setFormData({
            name: '',
            price: '',
            durationMinutes: '60',
            requiredSpecialtyId: ''
        });
        setShowCreateModal(true);
    };

    const handleEditClick = (service: ServiceResponse) => {
        setIsEditing(true);
        setSelectedServiceId(service.id);
        setFormData({
            name: service.name,
            price: service.price.toString(),
            durationMinutes: service.durationMinutes.toString(),
            requiredSpecialtyId: service.requiredSpecialtyId || ''
        });
        setShowCreateModal(true);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitError(null);

        // --- VALIDATION START ---
        if (!formData.name.trim()) {
            setSubmitError('El nombre del servicio es requerido');
            setIsSubmitting(false);
            return;
        }
        if (!formData.price || parseFloat(formData.price) <= 0) {
            setSubmitError('El precio debe ser mayor a 0');
            setIsSubmitting(false);
            return;
        }
        if (!formData.durationMinutes || parseInt(formData.durationMinutes) <= 0) {
            setSubmitError('La duración debe ser mayor a 0 minutos');
            setIsSubmitting(false);
            return;
        }
        // --- VALIDATION END ---

        try {
            const payload = {
                salonId: salonId!,
                name: formData.name,
                price: parseFloat(formData.price),
                durationMinutes: parseInt(formData.durationMinutes),
                requiredSpecialtyId: formData.requiredSpecialtyId || undefined
            };

            if (isEditing && selectedServiceId) {
                await serviceApi.updateService(selectedServiceId, payload);
            } else {
                await serviceApi.createService(payload);
            }

            // Success!
            setShowCreateModal(false);
            toast.success(isEditing ? 'Servicio actualizado exitosamente' : 'Servicio creado exitosamente');

            // Reset form
            setFormData({
                name: '',
                price: '',
                durationMinutes: '60',
                requiredSpecialtyId: ''
            });
            setIsEditing(false);
            setSelectedServiceId(null);

            // Reload services
            loadServices();
        } catch (error: any) {
            console.error('Error submitting service:', error);
            setSubmitError(error.response?.data?.message || 'Error al guardar servicio');
        } finally {
            setIsSubmitting(false);
        }
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('es-CO', {
            style: 'currency',
            currency: 'COP',
            minimumFractionDigits: 0
        }).format(value);
    };

    const formatDuration = (minutes: number) => {
        if (minutes < 60) return `${minutes} min`;
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        return mins > 0 ? `${hours}h ${mins}min` : `${hours}h`;
    };

    return (
        <div className="p-6">
            {/* Header */}
            <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900">Servicios</h1>
                <p className="text-gray-600 mt-1">Catálogo de servicios del salón</p>
            </div>

            {/* Toolbar */}
            <div className="mb-6 flex items-center justify-between gap-4">
                {/* Search */}
                <div className="flex-1 max-w-md">
                    <div className="relative">
                        <input
                            type="text"
                            placeholder="Buscar servicios..."
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                            value={searchQuery}
                            onChange={e => setSearchQuery(e.target.value)}
                        />
                        <svg
                            className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                        </svg>
                    </div>
                </div>

                {/* Create Button */}
                <button
                    onClick={handleCreateClick}
                    className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium cursor-pointer"
                >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    Nuevo Servicio
                </button>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                {isLoading ? (
                    <TableSkeleton rows={5} columns={5} />
                ) : filteredServices.length === 0 ? (
                    <EmptyState
                        icon={Scissors}
                        title={searchQuery ? 'No se encontraron servicios' : 'No hay servicios registrados'}
                        description={searchQuery ? 'Intenta con otro término de búsqueda.' : 'Comienza agregando tu primer servicio. Los servicios son esenciales para definir qué ofrece tu salón y poder agendar citas.'}
                        action={!searchQuery ? {
                            label: 'Agregar Primer Servicio',
                            onClick: handleCreateClick
                        } : undefined}
                        secondaryAction={searchQuery ? {
                            label: 'Limpiar Búsqueda',
                            onClick: () => setSearchQuery('')
                        } : undefined}
                    />
                ) : (
                    <>
                        {/* Mobile View - Cards */}
                        <div className="md:hidden divide-y divide-gray-200">
                            {filteredServices.map(service => (
                                <div key={service.id} className="p-4 space-y-3">
                                    <div className="flex justify-between items-start">
                                        <div>
                                            <h3 className="font-medium text-gray-900">{service.name}</h3>
                                            <p className="text-sm text-gray-500 mt-1">{formatDuration(service.durationMinutes)}</p>
                                        </div>
                                        <span className="text-lg font-bold text-gray-900">
                                            {formatCurrency(service.price)}
                                        </span>
                                    </div>

                                    <div className="flex items-center justify-between text-sm">
                                        <div className="text-gray-500">
                                            {service.requiredSpecialtyId ? (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                    {specialties.find(s => s.id === service.requiredSpecialtyId)?.name || 'Cargando...'}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400 italic">Universal</span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${service.isActive
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                {service.isActive ? 'Activo' : 'Inactivo'}
                                            </span>
                                            <button
                                                onClick={() => handleEditClick(service)}
                                                className="text-emerald-600 hover:text-emerald-900 font-medium cursor-pointer"
                                            >
                                                Editar
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Desktop View - Table */}
                        <table className="min-w-full divide-y divide-gray-200 hidden md:table">
                            <thead className="table-header-premium">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Servicio
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Duración
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Precio
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Especialidad Req.
                                    </th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Estado
                                    </th>
                                    <th className="relative px-6 py-3">
                                        <span className="sr-only">Editar</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredServices.map(service => (
                                    <tr key={service.id} className="table-row-hover">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="font-medium text-gray-900">
                                                {service.name}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDuration(service.durationMinutes)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                                            {formatCurrency(service.price)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {service.requiredSpecialtyId ? (
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                                    {specialties.find(s => s.id === service.requiredSpecialtyId)?.name || 'Cargando...'}
                                                </span>
                                            ) : (
                                                <span className="text-gray-400 font-italic">Universal</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${service.isActive
                                                ? 'bg-green-100 text-green-800'
                                                : 'bg-gray-100 text-gray-800'
                                                }`}>
                                                {service.isActive ? 'Activo' : 'Inactivo'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button
                                                onClick={() => handleEditClick(service)}
                                                className="text-emerald-600 hover:text-emerald-900 cursor-pointer"
                                            >
                                                Editar
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}
            </div>

            {/* Create/Edit Modal */}
            {showCreateModal && (
                <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowCreateModal(false)}>
                    <div className="modal-content max-w-md p-6 space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="text-xl font-bold text-gray-900">
                                {isEditing ? 'Editar Servicio' : 'Nuevo Servicio'}
                            </h3>
                            <button
                                onClick={() => setShowCreateModal(false)}
                                className="text-gray-400 hover:text-gray-600 cursor-pointer"
                            >
                                ✕
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Nombre del Servicio</label>
                                <input
                                    type="text"
                                    required
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                    value={formData.name}
                                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                                    disabled={isSubmitting}
                                    placeholder="Ej: Corte de Dama"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Precio (COP)</label>
                                <input
                                    type="number"
                                    required
                                    min="0"
                                    step="1000"
                                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                                    value={formData.price}
                                    onChange={e => setFormData({ ...formData, price: e.target.value })}
                                    disabled={isSubmitting}
                                    placeholder="50000"
                                />
                            </div>

                            <SearchableSelect
                                label="Duración (minutos)"
                                placeholder="Seleccionar duración..."
                                options={[
                                    { value: "15", label: "15 minutos" },
                                    { value: "30", label: "30 minutos" },
                                    { value: "45", label: "45 minutos" },
                                    { value: "60", label: "1 hora" },
                                    { value: "90", label: "1 hora 30 min" },
                                    { value: "120", label: "2 horas" },
                                    { value: "150", label: "2 horas 30 min" },
                                    { value: "180", label: "3 horas" },
                                ]}
                                value={formData.durationMinutes}
                                onChange={val => setFormData({ ...formData, durationMinutes: val })}
                                disabled={isSubmitting}
                            />

                            <SearchableSelect
                                label="Especialidad Requerida"
                                placeholder="Ninguna (Servicio Universal)"
                                options={[
                                    { value: "", label: "Ninguna (Servicio Universal)" },
                                    ...specialties.map(s => ({ value: s.id, label: s.name }))
                                ]}
                                value={formData.requiredSpecialtyId}
                                onChange={val => setFormData({ ...formData, requiredSpecialtyId: val })}
                                disabled={isSubmitting}
                            />

                            {submitError && (
                                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                                    <p className="text-sm text-red-600">{submitError}</p>
                                </div>
                            )}

                            <div className="flex gap-3 pt-2">
                                <button
                                    type="button"
                                    onClick={() => setShowCreateModal(false)}
                                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium cursor-pointer"
                                    disabled={isSubmitting}
                                >
                                    Cancelar
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors font-medium disabled:opacity-50 cursor-pointer"
                                    disabled={isSubmitting}
                                >
                                    {isSubmitting ? 'Guardando...' : (isEditing ? 'Actualizar Servicio' : 'Crear Servicio')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Prerequisite Alert Modal */}
            <PrerequisiteAlertModal
                isOpen={showPrerequisiteAlert}
                onClose={() => setShowPrerequisiteAlert(false)}
                title="Especialidades Requeridas"
                message="No puedes crear servicios sin especialidades. Define al menos una especialidad (ej. Corte, Color, Uñas) para organizar tu catálogo de servicios."
                redirectPath="/app/settings"
                redirectLabel="Ir a Configuración"
            />
        </div>
    );
};

export default ServiceListPage;
